import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Plus, Edit2, Trash2, LogOut, Image, Save, X, Upload, 
  List, Calendar, CheckCircle, Lightbulb, DollarSign, MapPin,
  Clock, Tag, ChevronDown, ChevronUp, Eye
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const [tours, setTours] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingTour, setEditingTour] = useState(null);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('basic');
  const [uploadingKey, setUploadingKey] = useState(null);
  const [uploadError, setUploadError] = useState('');
  const navigate = useNavigate();

  const emptyForm = {
    title: '',
    image: '',
    gallery: ['', '', '', '', ''],
    locations: '',
    duration: '',
    transport: 'Ida y Vuelta',
    originalPrice: '',
    salePrice: '',
    childPrice: '',
    priceLabel: 'por persona',
    extraPrice: '',
    isOffer: false,
    category: 'Excursiones',
    tag: '',
    description: '',
    includes: [''],
    schedule: [{ title: '', desc: '' }],
    recommendations: ''
  };

  const [formData, setFormData] = useState(emptyForm);

  useEffect(() => {
    const isAuth = localStorage.getItem('adminAuth');
    if (!isAuth) {
      navigate('/admin');
      return;
    }
    fetchTours();
  }, [navigate]);

  const fetchTours = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/tours`);
      setTours(response.data);
    } catch (err) {
      console.error('Error fetching tours:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  const handleImageUpload = async (e, field = 'image', index = null) => {
    const file = e.target.files[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setUploadError('El archivo debe ser una imagen (JPG, PNG, WEBP...).');
      e.target.value = '';
      return;
    }

    const key = field === 'gallery' ? `gallery-${index}` : 'image';
    setUploadError('');
    setUploadingKey(key);

    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${BACKEND_URL}/api/upload-image`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const url = response.data.url;

      if (field === 'image') {
        setFormData(prev => ({ ...prev, image: url }));
      } else if (field === 'gallery' && index !== null) {
        setFormData(prev => {
          const newGallery = [...prev.gallery];
          newGallery[index] = url;
          return { ...prev, gallery: newGallery };
        });
      }
    } catch (err) {
      console.error('Error uploading image:', err);
      setUploadError('Error al subir la imagen. Intenta de nuevo.');
    } finally {
      setUploadingKey(null);
      e.target.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.image || !formData.image.trim()) {
      setUploadError('Debes subir o indicar una imagen principal antes de guardar.');
      setActiveTab('images');
      return;
    }

    setSaving(true);

    const tourData = {
      title: formData.title,
      image: formData.image,
      gallery: formData.gallery.filter(g => g.trim() !== ''),
      locations: formData.locations.split(',').map(l => l.trim()).filter(l => l),
      duration: formData.duration,
      transport: formData.transport,
      originalPrice: formData.originalPrice ? parseFloat(formData.originalPrice) : null,
      salePrice: parseFloat(formData.salePrice),
      childPrice: formData.childPrice ? parseFloat(formData.childPrice) : null,
      priceLabel: formData.priceLabel || null,
      extraPrice: formData.extraPrice || null,
      isOffer: formData.isOffer,
      category: formData.category,
      tag: formData.tag || null,
      description: formData.description,
      includes: formData.includes.filter(i => i.trim() !== ''),
      schedule: formData.schedule.filter(s => s.title.trim() !== ''),
      recommendations: formData.recommendations || null
    };

    try {
      if (editingTour) {
        await axios.put(`${BACKEND_URL}/api/tours/${editingTour.id}`, tourData);
      } else {
        await axios.post(`${BACKEND_URL}/api/tours`, tourData);
      }
      fetchTours();
      resetForm();
    } catch (err) {
      console.error('Error saving tour:', err);
      alert('Error al guardar el tour');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (tour) => {
    setEditingTour(tour);
    setFormData({
      title: tour.title || '',
      image: tour.image || '',
      gallery: tour.gallery?.length > 0 ? tour.gallery : [''],
      locations: tour.locations?.join(', ') || '',
      duration: tour.duration || '',
      transport: tour.transport || 'Ida y Vuelta',
      originalPrice: tour.originalPrice || '',
      salePrice: tour.salePrice || '',
      childPrice: tour.childPrice || '',
      priceLabel: tour.priceLabel || 'por persona',
      extraPrice: tour.extraPrice || '',
      isOffer: tour.isOffer || false,
      category: tour.category || 'Excursiones',
      tag: tour.tag || '',
      description: tour.description || '',
      includes: tour.includes?.length > 0 ? tour.includes : [''],
      schedule: tour.schedule?.length > 0 ? tour.schedule : [{ title: '', desc: '' }],
      recommendations: tour.recommendations || ''
    });
    setShowForm(true);
    setActiveTab('basic');
  };

  const handleDelete = async (tourId) => {
    if (!window.confirm('¿Estás seguro de eliminar este tour?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/tours/${tourId}`);
      fetchTours();
    } catch (err) {
      console.error('Error deleting tour:', err);
      alert('Error al eliminar el tour');
    }
  };

  const resetForm = () => {
    setFormData(emptyForm);
    setEditingTour(null);
    setShowForm(false);
    setActiveTab('basic');
  };

  // Array handlers
  const addArrayItem = (field) => {
    if (field === 'schedule') {
      setFormData({ ...formData, schedule: [...formData.schedule, { title: '', desc: '' }] });
    } else {
      setFormData({ ...formData, [field]: [...formData[field], ''] });
    }
  };

  const removeArrayItem = (field, index) => {
    const newArray = [...formData[field]];
    newArray.splice(index, 1);
    setFormData({ ...formData, [field]: newArray });
  };

  const updateArrayItem = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData({ ...formData, [field]: newArray });
  };

  const updateScheduleItem = (index, key, value) => {
    const newSchedule = [...formData.schedule];
    newSchedule[index] = { ...newSchedule[index], [key]: value };
    setFormData({ ...formData, schedule: newSchedule });
  };

  const tabs = [
    { id: 'basic', label: 'Información Básica', icon: List },
    { id: 'pricing', label: 'Precios', icon: DollarSign },
    { id: 'images', label: 'Imágenes', icon: Image },
    { id: 'includes', label: 'Qué Incluye', icon: CheckCircle },
    { id: 'schedule', label: 'Itinerario', icon: Calendar },
    { id: 'extra', label: 'Extras', icon: Lightbulb },
  ];

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-800">Panel de Administración</h1>
            <p className="text-sm text-gray-500">MR Excursion Punta Cana - CMS</p>
          </div>
          <div className="flex items-center gap-4">
            <a href="/" target="_blank" className="flex items-center gap-1 text-teal-500 hover:text-teal-600 text-sm">
              <Eye size={16} />
              Ver sitio
            </a>
            <button
              onClick={handleLogout}
              className="flex items-center gap-2 text-gray-600 hover:text-red-500 transition-colors"
            >
              <LogOut size={18} />
              Salir
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-800">
            Tours ({tours.length})
          </h2>
          <button
            onClick={() => { setShowForm(true); setEditingTour(null); setFormData(emptyForm); }}
            className="flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-4 py-2 rounded-lg transition-colors"
          >
            <Plus size={18} />
            Agregar Tour
          </button>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto">
            <div className="bg-white rounded-xl w-full max-w-4xl my-8">
              {/* Form Header */}
              <div className="p-6 border-b flex items-center justify-between sticky top-0 bg-white rounded-t-xl z-10">
                <h3 className="text-lg font-semibold">
                  {editingTour ? 'Editar Tour' : 'Nuevo Tour'}
                </h3>
                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                  <X size={24} />
                </button>
              </div>

              {/* Tabs */}
              <div className="border-b px-6 flex gap-1 overflow-x-auto">
                {tabs.map(tab => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.id 
                        ? 'border-teal-500 text-teal-600' 
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <tab.icon size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="p-6">
                {/* Basic Info Tab */}
                {activeTab === 'basic' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Título del Tour *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Descripción *
                      </label>
                      <textarea
                        value={formData.description}
                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                        rows={4}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                        required
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Ubicaciones (separadas por coma) *
                        </label>
                        <input
                          type="text"
                          value={formData.locations}
                          onChange={(e) => setFormData({ ...formData, locations: e.target.value })}
                          placeholder="Punta Cana, Santo Domingo"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Duración *
                        </label>
                        <input
                          type="text"
                          value={formData.duration}
                          onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                          placeholder="1 Día, 4 Horas"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Transporte
                        </label>
                        <input
                          type="text"
                          value={formData.transport}
                          onChange={(e) => setFormData({ ...formData, transport: e.target.value })}
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Categoría
                        </label>
                        <select
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                        >
                          <option>Excursiones</option>
                          <option>Aventura</option>
                          <option>Vida Nocturna</option>
                          <option>Traslados</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Etiqueta
                        </label>
                        <input
                          type="text"
                          value={formData.tag}
                          onChange={(e) => setFormData({ ...formData, tag: e.target.value })}
                          placeholder="VIP, Aventura, Por Mar"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Pricing Tab */}
                {activeTab === 'pricing' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio Original (tachado)
                        </label>
                        <input
                          type="number"
                          value={formData.originalPrice}
                          onChange={(e) => setFormData({ ...formData, originalPrice: e.target.value })}
                          placeholder="89"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio Actual *
                        </label>
                        <input
                          type="number"
                          value={formData.salePrice}
                          onChange={(e) => setFormData({ ...formData, salePrice: e.target.value })}
                          placeholder="55"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                          required
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Precio Niños
                        </label>
                        <input
                          type="number"
                          value={formData.childPrice}
                          onChange={(e) => setFormData({ ...formData, childPrice: e.target.value })}
                          placeholder="45"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Etiqueta de Precio
                        </label>
                        <input
                          type="text"
                          value={formData.priceLabel}
                          onChange={(e) => setFormData({ ...formData, priceLabel: e.target.value })}
                          placeholder="por persona"
                          className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Precio Extra (texto)
                      </label>
                      <input
                        type="text"
                        value={formData.extraPrice}
                        onChange={(e) => setFormData({ ...formData, extraPrice: e.target.value })}
                        placeholder="$30 por persona adicional"
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>

                    <div className="flex items-center gap-2 pt-2">
                      <input
                        type="checkbox"
                        id="isOffer"
                        checked={formData.isOffer}
                        onChange={(e) => setFormData({ ...formData, isOffer: e.target.checked })}
                        className="w-5 h-5 text-teal-500 rounded"
                      />
                      <label htmlFor="isOffer" className="text-sm text-gray-700">
                        Mostrar etiqueta "¡Oferta!" (se muestra automáticamente si hay precio original)
                      </label>
                    </div>
                  </div>
                )}

                {/* Images Tab */}
                {activeTab === 'images' && (
                  <div className="space-y-6">
                    {uploadError && (
                      <div data-testid="upload-error-banner" className="px-4 py-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
                        {uploadError}
                      </div>
                    )}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Imagen Principal *
                      </label>
                      <div className="flex items-center gap-4">
                        {formData.image && (
                          <img src={formData.image} alt="Preview" className="w-32 h-24 object-cover rounded" />
                        )}
                        <label className={`flex items-center gap-2 px-4 py-2 rounded-lg ${uploadingKey === 'image' ? 'bg-gray-200 cursor-wait opacity-70' : 'bg-gray-100 hover:bg-gray-200 cursor-pointer'}`}>
                          <Upload size={18} />
                          <span>{uploadingKey === 'image' ? 'Subiendo...' : 'Subir imagen'}</span>
                          <input data-testid="main-image-upload-input" type="file" accept="image/*" disabled={uploadingKey === 'image'} onChange={(e) => handleImageUpload(e, 'image')} className="hidden" />
                        </label>
                      </div>
                      <input
                        type="text"
                        value={formData.image}
                        onChange={(e) => setFormData({ ...formData, image: e.target.value })}
                        placeholder="O pega una URL de imagen"
                        className="mt-2 w-full px-4 py-2 border rounded-lg text-sm"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-3">
                        Galería de Imágenes (hasta 10 fotos)
                      </label>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                        {formData.gallery.map((img, index) => (
                          <div key={index} className="relative bg-gray-50 rounded-lg p-3 border-2 border-dashed border-gray-200 hover:border-teal-300 transition-colors">
                            {img ? (
                              <div className="relative">
                                <img src={img} alt="" className="w-full h-24 object-cover rounded" />
                                <button 
                                  type="button" 
                                  onClick={() => updateArrayItem('gallery', index, '')}
                                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                                >
                                  <X size={14} />
                                </button>
                              </div>
                            ) : (
                              <label className={`flex flex-col items-center justify-center h-24 ${uploadingKey === `gallery-${index}` ? 'cursor-wait opacity-70' : 'cursor-pointer'}`}>
                                <Upload size={24} className="text-gray-400 mb-1" />
                                <span className="text-xs text-gray-500">{uploadingKey === `gallery-${index}` ? 'Subiendo...' : `Foto ${index + 1}`}</span>
                                <input data-testid={`gallery-upload-input-${index}`} type="file" accept="image/*" disabled={uploadingKey === `gallery-${index}`} onChange={(e) => handleImageUpload(e, 'gallery', index)} className="hidden" />
                              </label>
                            )}
                            {!img && (
                              <input
                                type="text"
                                value={img}
                                onChange={(e) => updateArrayItem('gallery', index, e.target.value)}
                                placeholder="O pega URL"
                                className="mt-2 w-full px-2 py-1 border rounded text-xs"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                      <div className="flex gap-2 mt-4">
                        {formData.gallery.length < 10 && (
                          <button type="button" onClick={() => addArrayItem('gallery')} className="text-teal-500 text-sm font-medium hover:text-teal-600">
                            + Agregar más fotos
                          </button>
                        )}
                        {formData.gallery.length > 1 && (
                          <button 
                            type="button" 
                            onClick={() => {
                              const filtered = formData.gallery.filter(g => g.trim() !== '');
                              setFormData({ ...formData, gallery: filtered.length > 0 ? filtered : [''] });
                            }} 
                            className="text-gray-500 text-sm font-medium hover:text-gray-700 ml-auto"
                          >
                            Limpiar vacíos
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )}

                {/* Includes Tab */}
                {activeTab === 'includes' && (
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      ¿Qué incluye esta excursión?
                    </label>
                    {formData.includes.map((item, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <CheckCircle size={16} className="text-teal-500" />
                        <input
                          type="text"
                          value={item}
                          onChange={(e) => updateArrayItem('includes', index, e.target.value)}
                          placeholder="Ej: Transporte ida y vuelta"
                          className="flex-1 px-4 py-2 border rounded-lg"
                        />
                        {formData.includes.length > 1 && (
                          <button type="button" onClick={() => removeArrayItem('includes', index)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                            <X size={16} />
                          </button>
                        )}
                      </div>
                    ))}
                    <button type="button" onClick={() => addArrayItem('includes')} className="text-teal-500 text-sm font-medium">
                      + Agregar otro elemento
                    </button>
                  </div>
                )}

                {/* Schedule Tab */}
                {activeTab === 'schedule' && (
                  <div className="space-y-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Itinerario del Tour
                    </label>
                    {formData.schedule.map((step, index) => (
                      <div key={index} className="p-4 bg-gray-50 rounded-lg space-y-2">
                        <div className="flex items-center gap-2">
                          <span className="w-8 h-8 bg-teal-500 text-white rounded-full flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </span>
                          <input
                            type="text"
                            value={step.title}
                            onChange={(e) => updateScheduleItem(index, 'title', e.target.value)}
                            placeholder="Título del paso"
                            className="flex-1 px-3 py-2 border rounded-lg"
                          />
                          {formData.schedule.length > 1 && (
                            <button type="button" onClick={() => removeArrayItem('schedule', index)} className="p-2 text-red-500 hover:bg-red-50 rounded">
                              <X size={16} />
                            </button>
                          )}
                        </div>
                        <textarea
                          value={step.desc}
                          onChange={(e) => updateScheduleItem(index, 'desc', e.target.value)}
                          placeholder="Descripción de este paso"
                          rows={2}
                          className="w-full px-3 py-2 border rounded-lg text-sm"
                        />
                      </div>
                    ))}
                    <button type="button" onClick={() => addArrayItem('schedule')} className="text-teal-500 text-sm font-medium">
                      + Agregar otro paso
                    </button>
                  </div>
                )}

                {/* Extra Tab */}
                {activeTab === 'extra' && (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Recomendaciones
                      </label>
                      <textarea
                        value={formData.recommendations}
                        onChange={(e) => setFormData({ ...formData, recommendations: e.target.value })}
                        placeholder="Ej: Traje de baño, protector solar, toalla..."
                        rows={3}
                        className="w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-teal-500"
                      />
                    </div>
                  </div>
                )}

                {/* Submit Buttons */}
                <div className="flex gap-3 pt-6 mt-6 border-t">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving}
                    className="flex-1 flex items-center justify-center gap-2 bg-teal-500 hover:bg-teal-600 disabled:bg-teal-300 text-white px-4 py-3 rounded-lg"
                  >
                    <Save size={18} />
                    {saving ? 'Guardando...' : 'Guardar Tour'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Tours List */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando tours...</div>
        ) : tours.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-xl">
            <Image className="mx-auto text-gray-300 mb-4" size={48} />
            <p className="text-gray-500">No hay tours todavía</p>
            <p className="text-sm text-gray-400 mb-4">Haz clic en "Agregar Tour" para crear uno</p>
            <button
              onClick={() => { setShowForm(true); setFormData(emptyForm); }}
              className="inline-flex items-center gap-2 bg-teal-500 hover:bg-teal-600 text-white px-6 py-3 rounded-lg"
            >
              <Plus size={18} />
              Agregar Tu Primer Tour
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tours.map((tour) => (
              <div key={tour.id} className="bg-white rounded-lg shadow-sm overflow-hidden">
                <div className="relative">
                  <img src={tour.image} alt={tour.title} className="w-full h-40 object-cover" />
                  {tour.tag && (
                    <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs px-2 py-1 rounded">
                      {tour.tag}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-semibold text-gray-800 mb-1">{tour.title}</h3>
                  <p className="text-sm text-gray-500 mb-2">{tour.locations?.join(', ')}</p>
                  <p className="text-xs text-gray-400 mb-3 line-clamp-2">{tour.description}</p>
                  <div className="flex items-center gap-2 mb-3">
                    {tour.originalPrice && (
                      <span className="text-gray-400 line-through text-sm">${tour.originalPrice}</span>
                    )}
                    <span className="text-xl font-bold text-teal-500">${tour.salePrice}</span>
                    {tour.priceLabel && (
                      <span className="text-xs text-gray-500">{tour.priceLabel}</span>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handleEdit(tour)}
                      className="flex-1 flex items-center justify-center gap-1 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
                    >
                      <Edit2 size={14} />
                      Editar
                    </button>
                    <button
                      onClick={() => handleDelete(tour.id)}
                      className="flex items-center justify-center gap-1 px-3 py-2 bg-red-50 hover:bg-red-100 text-red-600 rounded-lg text-sm"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
